import React, { useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import LotteryCodeField from '../../components/LotteryCodeField';
import SupervisorGroupCodeField from '../../components/SupervisorGroupCodeField';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import DateField from '../../components/DateField';
import { fetchSupervisorAccumulatedSummary } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import './SupervisorAccumulatedSummaryPage.css';

const documentTypeOptions = [
  { value: '', label: 'Tout' },
  { value: 'online', label: 'Online' },
  { value: 'local', label: 'Local' },
  { value: 'local_on_time', label: 'Local On Time' },
  { value: 'local_overtime', label: 'Local Overtime' },
];
// 'supervisor' exclu — il n'y en a qu'UN cote superviseur (lui-meme).
const displayModeOptions = [
  { value: 'global', label: 'Global' },
  { value: 'bank', label: 'Banque' },
  { value: 'group', label: 'Groupe' },
  { value: 'lottery', label: 'Loterie' },
  { value: 'date', label: 'Date' },
];

const DIM_PREFIX = { bank: 'Banque', group: 'Groupe', lottery: 'Loterie', date: 'Date', global: null };

const fmt = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
const fmtPct = (n) => `${(n || 0).toFixed(2)}%`;

const SupervisorAccumulatedSummaryPage = () => {
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [lotteryFrom, setLotteryFrom] = useState('');
  const [lotteryFromName, setLotteryFromName] = useState('');
  const [lotteryUp, setLotteryUp] = useState('');
  const [lotteryUpName, setLotteryUpName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [displayMode, setDisplayMode] = useState('global');
  // Filtre Groupe / Banque optionnel, mutuellement exclusif (vide = toutes ses banks).
  const [groupId, setGroupId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await fetchSupervisorAccumulatedSummary({
        dateFrom, dateTo, lotteryFrom, lotteryUp, documentType, displayMode, orderBy: 'code',
        group: groupId, bank: bankId,
      });
      setReport(data);
    } catch (err) {
      setError('Recherche impossible.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const blocks = report?.blocks || [];
  const dimPrefix = DIM_PREFIX[report?.display_mode || 'global'];

  return (
    <SupervisorLayout>
      <h2 className="svas-title">Résumé accumulé</h2>

      <div className="svas-card">
        <form onSubmit={handleSearch} className="svas-form">
          <div className="svas-row">
            <label className="svas-label">De</label>
            <DateField className="svas-input" value={dateFrom} onChange={setDateFrom} />
            <label className="svas-label svas-label-to">À</label>
            <DateField className="svas-input" value={dateTo} onChange={setDateTo} />
          </div>

          <div className="svas-row">
            <label className="svas-label">Loterie De</label>
            <LotteryCodeField
              value={lotteryFrom}
              name={lotteryFromName}
              onChange={(code) => { setLotteryFrom(code); setLotteryFromName(''); }}
              onResolve={(code, nm) => { setLotteryFrom(code); setLotteryFromName(nm); }}
              inputClass="svas-input svas-input-sm"
              iconClass="svas-icon-btn"
              nameClass="svas-scope-name"
            />

            <label className="svas-label svas-label-to">Loterie À</label>
            <LotteryCodeField
              value={lotteryUp}
              name={lotteryUpName}
              onChange={(code) => { setLotteryUp(code); setLotteryUpName(''); }}
              onResolve={(code, nm) => { setLotteryUp(code); setLotteryUpName(nm); }}
              inputClass="svas-input svas-input-sm"
              iconClass="svas-icon-btn"
              nameClass="svas-scope-name"
            />
          </div>

          <div className="svas-row">
            <label className="svas-label">Groupe</label>
            <SupervisorGroupCodeField
              value={groupId}
              name={groupName}
              onChange={(id) => { setGroupId(id); setGroupName(''); }}
              onResolve={(id, nm) => {
                setGroupId(id); setGroupName(nm);
                if (id) { setBankId(''); setBankName(''); }
              }}
              inputClass="svas-input svas-input-sm"
              iconClass="svas-icon-btn"
              nameClass="svas-scope-name"
            />

            <label className="svas-label svas-label-to">Banque</label>
            <SupervisorBankCodeField
              value={bankId}
              name={bankName}
              onChange={(id) => { setBankId(id); setBankName(''); }}
              onResolve={(id, nm) => {
                setBankId(id); setBankName(nm);
                if (id) { setGroupId(''); setGroupName(''); }
              }}
              inputClass="svas-input svas-input-sm"
              iconClass="svas-icon-btn"
              nameClass="svas-scope-name"
            />
          </div>

          <div className="svas-row">
            <label className="svas-label">Type</label>
            <select className="svas-select" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              {documentTypeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <label className="svas-label svas-label-to">Affichage</label>
            <select className="svas-select" value={displayMode} onChange={(e) => setDisplayMode(e.target.value)}>
              {displayModeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {error && <p className="svas-error">{error}</p>}
          <button type="submit" className="svas-search-btn" disabled={loading}>
            {loading ? 'Recherche...' : 'Chercher'}
          </button>
        </form>
      </div>

      {report && (
        <div className="svas-results">
          {blocks.length === 0 && <p className="svas-empty">Aucune vente sur cette période.</p>}

          {blocks.map((block) => (
            <div key={block.code} className="svas-card svas-block">
              {dimPrefix && <p className="svas-block-title">{dimPrefix} : {block.name}</p>}

              <p className="svas-section-title">Ventes</p>
              {block.sales.map((row) => (
                <div key={row.label} className="svas-line">
                  <span>{row.label}</span>
                  <span className="svas-money">{fmt(row.amount)}</span>
                  <span className="svas-pct">{row.pct ? fmtPct(row.pct) : ''}</span>
                </div>
              ))}
              <div className="svas-line svas-line-bold">
                <span>Total ventes</span>
                <span className="svas-money">{fmt(block.sales_total)}</span>
                <span />
              </div>

              <p className="svas-section-title">Gains payés</p>
              {block.paid.map((cat) => (
                <div key={cat.label} className="svas-paid-cat">
                  <p className="svas-paid-header">{cat.label}</p>
                  {cat.items.map((item) => (
                    <div key={item.name} className="svas-line">
                      <span>{item.name}</span>
                      <span className="svas-money">{fmt(item.amount)}</span>
                      <span className="svas-pct">{item.pct ? fmtPct(item.pct) : ''}</span>
                    </div>
                  ))}
                  <div className="svas-line svas-line-bold">
                    <span>Sous-total</span>
                    <span className="svas-money">{fmt(cat.total)}</span>
                    <span className="svas-pct">{fmtPct(cat.pct)}</span>
                  </div>
                </div>
              ))}

              <div className="svas-totals">
                <div><span>Total gains</span><span>{fmt(block.total_payed)}</span></div>
                <div className="svas-totals-strong">
                  <span>{block.gross >= 0 ? 'Gain' : 'Perte'} (Brut)</span><span>{fmt(block.gross)}</span>
                </div>
                <div><span>Commission</span><span>{fmt(block.minus_sale_pct)}</span></div>
                <div className="svas-totals-strong">
                  <span>{block.net >= 0 ? 'Gain' : 'Perte'} (Net)</span><span>{fmt(block.net)}</span>
                </div>
                <div className="svas-totals-final">
                  <span>Résultat final</span><span>{fmt(block.final_result)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorAccumulatedSummaryPage;
